import { BaseUniDriver } from '@wix/wix-ui-test-utils/base-driver';
import { baseUniDriverFactory } from '../../testkitUtils/unidriver';
import { UniDriver } from '@wix/wix-ui-test-utils/unidriver';
import { RatingsInputDataHooks, RatingsInputDataKeys } from './dataHooks';
import { errorMessageWrapperDriverFactory } from '../internal/ErrorMessageWrapper/ErrorMessageWrapper.driver';
import { dataHook } from '../../testkitUtils/utils';

export interface RatingsInputDriver extends BaseUniDriver {
  getValue(): Promise<number>;
  clickStarAt(idx: number): Promise<void>;
  isDisabled(): Promise<boolean>;
  getLabelLayout(): Promise<string>;
  getSize(): Promise<'small' | 'large'>;
  hasError(): Promise<boolean>;
  getReactionLabel(): Promise<string>;
  getErrorMessage(): Promise<string>;
}

export const ratingsInputDriverFactory = (
  base: UniDriver,
): RatingsInputDriver => {
  return {
    ...baseUniDriverFactory(base),
    /**
     * Returns currently selected rating.
     * @returns {Promise<number>}
     */
    getValue: async () => {
      try {
        const value = await base
          .$(
            `${dataHook(
              RatingsInputDataHooks.RatingStarWrapper,
            )} input:checked`,
          )
          .value();
        return parseInt(value, 10);
      } catch {
        return 0;
      }
    },
    /**
     * Imitate a click on a star at a given index.
     * @returns {Promise<void>}
     */
    clickStarAt: (idx: number) =>
      base
        .$$(dataHook(RatingsInputDataHooks.RatingStarWrapper))
        .get(idx)
        .click(),
    /**
     * Returns current reaction label.
     * @returns {Promise<string>}
     */
    getReactionLabel: () => {
      return base.$(dataHook(RatingsInputDataHooks.ReactionLabel)).text();
    },
    /**
     * Returns true if input is disabled.
     * @returns {Promise<boolean>}
     */
    isDisabled: async () => {
      const disabled = await base
        .$(dataHook(RatingsInputDataHooks.Container))
        .attr(RatingsInputDataKeys.Disabled);
      return disabled === 'true';
    },
    /**
     * Returns label layout value.
     * @returns {Promise<string>}
     */
    getLabelLayout: () => {
      return base
        .$(dataHook(RatingsInputDataHooks.Container))
        .attr(RatingsInputDataKeys.Layout);
    },
    /**
     * Returns input size.
     * @returns {Promise<string>}
     */
    getSize: async () => {
      return base
        .$(dataHook(RatingsInputDataHooks.Container))
        .attr(RatingsInputDataKeys.Size) as Promise<'small' | 'large'>;
    },
    /**
     * Returns true if input is in error state.
     * @returns {Promise<boolean>}
     */
    hasError: () => {
      const errorMessageWrapperDriver = errorMessageWrapperDriverFactory(base);
      return errorMessageWrapperDriver.isErrorVisible();
    },
    /**
     * Returns error message text.
     * @returns {Promise<string>}
     */ getErrorMessage: () => {
      const errorMessageWrapperDriver = errorMessageWrapperDriverFactory(base);
      return errorMessageWrapperDriver.getErrorMessage();
    },
  };
};
